import './Button.scss';
import React from 'react';

import Tooltip from '@/src/components/common/Elements/Tooltip/Tooltip';
import { cn } from '@/src/util/ui';

import { getButtonStyleSettings } from './Config';

export type ButtonProps = {
  onClick;
  buttonRef?;
  disabled?: boolean;
  buttonClassSettings?: {
    variant?: string;
    size?: string;
    weight?: string;
    position?: string;
    isActive?: boolean;
    activeStyles?: string;
  };
  id?: string;
  className?: string;
  disabledClass?: string;
  'data-testid'?: string | null;
  tooltip?: {
    place?: string;
    effect?: string;
    className?: string;
    content?: React.ReactNode;
  };
  stopPropagation?: boolean;
  children?: React.ReactNode;
  [key: string]: any;
};

export const Button = ({
  onClick,
  buttonRef = null,
  disabled = false,
  buttonClassSettings = {
    variant: 'style_unset',
    size: 'sm',
    weight: 'normal',
    position: 'center',
    isActive: false,
    activeStyles: cn('tw-bg-cfo-blue-600 tw-text-white'),
  },
  id = '',
  className = '',
  disabledClass = cn('disabled tw-pointer-events-auto tw-cursor-not-allowed tw-text-cfo-grey-600'),
  'data-testid': dataTestId = null,
  tooltip = null,
  stopPropagation = false,
  children,
  ...otherProps
}: ButtonProps) => {
  const handleClick = (e) => {
    if (stopPropagation) {
      e.stopPropagation();
    }

    onClick(e);
  };

  const defaultButtonSettings = getButtonStyleSettings(buttonClassSettings);

  const buttonProps = {
    ...otherProps,
    type: otherProps.type || 'button',
    className: cn(
      'tw-group',
      'tw-flex tw-items-center',
      'tw-border-solid tw-border-[1px]',
      defaultButtonSettings,
      className,
      disabled && disabledClass
    ),
    disabled,
    onClick: !disabled && onClick ? handleClick : null,
    'data-testid': dataTestId,
    'data-tip': tooltip ? true : false,
    'data-tooltip-id': tooltip ? `container--button_${id}` : null,
    'data-tip-disable': false,
  };

  return (
    <button ref={buttonRef} {...buttonProps}>
      {children}

      {tooltip && (
        <Tooltip
          id={`container--button_${id}`}
          className={tooltip.className}
          place={tooltip.place || 'bottom'}
          effect={tooltip.effect || 'solid'}
        >
          {tooltip.content}
        </Tooltip>
      )}
    </button>
  );
};

export default Button;
